import { ObjectEntityType } from '@predictiveindex/client-permissions-lib';
import { User } from '../core-data-model/User.model';
import { ResultBasics } from './ResultBasics.model';

export class Folder implements ResultBasics {
    readonly id: string;
    readonly name: string;
    readonly childrenIds: string[];
    readonly children: Folder[] = [];
    readonly parentId: string = '';
    readonly folderPath?: string;
    readonly folderType: FolderType;
    readonly folderNameIdentifier?: FolderNameIdentifier;

    constructor(startingValues?: Partial<Folder>) {
        Object.assign(this, startingValues);
    }

    equals(collection: Folder): boolean {
        if (!collection) {
            return false;
        }
        return this.id === collection.id;
    }

    makeFolderUpdate(): FolderUpdate {
        return new FolderUpdate({
            name: this.name,
            parentId: this.parentId,
            originalObject: this
        });
    }

    getLink(): string {
        return `/folders/${this.id}`;
    }
}

export class FolderWithAccessDetails extends Folder {
    constructor(startingValues?: Partial<FolderWithAccessDetails>) {
        super(startingValues);
        Object.assign(this, startingValues);
    }
    readonly directAccessUsers: User[] = [];
    readonly inheritedAccessUsers: User[] = [];
}

export class FolderUpdate {
    name = '';
    parentId: string;
    directAccessUsers: string[];
    originalObject?: Folder;

    constructor(startingValues?: Partial<FolderUpdate>) {
        Object.assign(this, startingValues);
    }

    nameHasBeenAltered() {
        return this.originalObject && this.originalObject.name !== this.name;
    }

    pathHasBeenAltered() {
        return (
            this.originalObject &&
            this.originalObject.parentId !== this.parentId
        );
    }
}

export type UserFolderAssociation = {
    userId: string;
    folderId: string;
};

export type FolderResponse = {
    parentFolderGuid: string;
    companyGuid: string;
    createdTime: string;
    modifiedTime: string;
    isAccessible: boolean;
    folderType: FolderType;
    folderNameIdentifier?: FolderNameIdentifier;
    children: FolderResponse[];
    folderIdentityId: number;
    companyIdentityId: number;
    folderName: string;
    folderPath: string;
    folderGuid: string;
    isScoringAllowed: boolean;
    folderContents?: FolderContents;
    folderDescription?: string;
};

export type SurveyUserFolder = {
    folderGuid: string;
    folderName: string;
    folderNameIdentifier?: FolderNameIdentifier;
};

export type FolderCreate = {
    parentFolderGuid: string;
    folderName: string;
    directAccessUsers?: string[];
};

export class BulkFolderResponse {
    results: { [key: string]: FolderTransactionResult }[];
    hasFailure: boolean;
}

export class FolderTransactionResult {
    identifier: string;
    messages: string[];
    isSuccess: boolean;
    count: number;
}

export type FolderPatchUpdate = {
    parentFolderGuid: string;
    folderGuid: string;
    folderName: string;
    companyGuid?: string;
};

export type EditFolderUserAccessRequest = {
    companyGuid: string;
    updates: FolderAccessUpdate[];
};

export type FolderAccessUpdate = {
    folderGuid: string;
    userGuid: string;
    operation: FolderAccessOperation;
};

export type EditFolderUserAccessResponse = {
    results: Record<string, FolderAccessUpdateResult>;
    hasFailure: boolean;
};

export type AccessibleFolders = {
    accessResult: FolderAccessResult;
    accessibleFoldersGuids: string[];
};

export type FolderAccessUpdateResult = {
    identifier: string;
    messages: string[];
    exception: string;
    isSuccess: boolean;
};

export type FolderDetailWithAccess = FolderResponse & {
    directAccessUserDetails: FolderUser[];
    inheritedAccessUserDetails: FolderUser[];
};

export type FolderUser = {
    userGuid: string;
    lastName: string;
    firstName: string;
};

export enum FolderType {
    Unknown = 0,
    Default = 1,
    Custom = 2,
    UncategorizedJobs = 3,
    UncategorizedEmployees = 4,
    SubFolder = 5
}

export enum FolderNameIdentifier {
    Unknown = 0,
    FolderRoot = 1,
    UncategorizedJobs = 2,
    UncategorizedEmployees = 3,
    Departments = 4,
    BusinessUnits = 5,
    Locations = 6,
    Prospects = 7
}

export enum FolderAccessOperation {
    Unknown = 0,
    Grant = 1,
    Revoke = 2
}

export enum FolderAccessResult {
    NoAccess = 0,
    FullAccess = 1,
    LimitedAccess = 2
}

export type FolderContents = {
    hasAnySurveyUsers: boolean;
    hasAnyJobs: boolean;
};

export type FolderEntityJob = {
    id: string;
    name: string;
    lastModified: Date;
};

export type FolderEntityEmployee = {
    id: string;
    firstName: string;
    lastName: string;
    lastModified: Date;
};

export type BulkChangeFolder = {
    targetFolderId: string;
    entities: EntityInfo[];
};

export type EntityInfo = {
    entityType: ObjectEntityType;
    entityId: string;
};

export type BulkChangeFolderResponse = {
    entitiesMoved: string[];
    entitiesUpdated: number;
    entitiesRequested: number;
    updatedFolderId: string;
};
